import { useState } from 'react';

import type { SimulatorChannel, SimulatorEvent } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';

import { SimulatorChat } from './channel-simulator-chat';
import { SimulatorLog } from './channel-simulator-log';
import { chatMessages } from './channel-simulator.events';
import { SIMULATOR_CHANNEL_LABELS } from './channel-simulator.types';

type EventsView = 'log' | 'chat';

const EVENTS_VIEWS: EventsView[] = ['log', 'chat'];
const EVENTS_VIEW_LABELS: Record<EventsView, string> = {
  log: 'Log',
  chat: 'Chat',
};

const rawPayload = (event: SimulatorEvent) => {
  if (event.kind === 'inbound') {
    return {
      title: 'Webhook body posted to the backend',
      body: event.webhook_body,
    };
  }
  if (event.request) {
    return {
      title: `Provider request the backend built: ${event.request.method} ${event.request.url}`,
      body: event.request.body,
    };
  }
  return null;
};

export const SimulatorEvents = ({
  events,
  channel,
}: {
  events: SimulatorEvent[];
  channel: SimulatorChannel;
}) => {
  const [view, setView] = useState<EventsView>('log');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const messages = chatMessages(events, channel);
  // The payload panel follows the view: an event the view does not list is not selected
  const shownEvents =
    view === 'log' ? events : messages.map((message) => message.event);
  const selectedEvent = shownEvents.find((event) => event.seq === selectedSeq);
  const payload = selectedEvent ? rawPayload(selectedEvent) : null;

  return (
    <YStack className="min-h-0 flex-1 gap-3">
      <XStack className="shrink-0 items-center gap-1">
        {EVENTS_VIEWS.map((option) => (
          <Button
            key={option}
            size="xs"
            variant={option === view ? 'secondary' : 'ghost'}
            aria-pressed={option === view}
            onClick={() => setView(option)}
          >
            {EVENTS_VIEW_LABELS[option]}
          </Button>
        ))}
        <span className="pl-2 text-xs text-foreground-subtle">
          {view === 'log'
            ? 'Every event of the session, both channels'
            : `Messages of the ${SIMULATOR_CHANNEL_LABELS[channel]} device`}
        </span>
      </XStack>
      <XStack className="min-h-0 flex-1 gap-3">
        <YStack className="min-h-0 min-w-0 flex-1">
          {view === 'log' ? (
            <SimulatorLog
              events={events}
              selectedSeq={selectedSeq}
              onSelect={setSelectedSeq}
            />
          ) : (
            <SimulatorChat
              messages={messages}
              selectedSeq={selectedSeq}
              onSelect={setSelectedSeq}
            />
          )}
        </YStack>
        <YStack className="w-96 shrink-0 gap-1 overflow-hidden rounded-lg border border-border bg-background-subtle">
          {payload ? (
            <>
              <span className="break-all px-3 pt-2 text-xs font-semibold text-foreground-subtle">
                {payload.title}
              </span>
              <Box className="min-h-0 flex-1 overflow-auto px-3 pb-2">
                <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed">
                  {JSON.stringify(payload.body, null, 2)}
                </pre>
              </Box>
            </>
          ) : (
            <span className="px-3 py-2 text-xs text-foreground-subtle">
              Select an event to see its raw payload.
            </span>
          )}
        </YStack>
      </XStack>
    </YStack>
  );
};
